import type { FileRecord } from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { deepestPointerWithin } from "./deepestPointerWithin";
import { snapCenterToCursor } from "./snapCenterToCursor";
import { TransferDragPill } from "./TransferDragPill";
import type { FileRecordCopyPayload } from "./types";
import { useTransferDispatcher } from "./useTransferDispatcher";

/**
 * Cursor coordinates resolved from the tracked pointer at drop time.
 * The dispatcher hands these off to the canvas-board handler so it can
 * call `screenToFlowPosition` without subscribing to dnd-kit internals.
 */
export interface CanvasBoardDropContext {
  screenX: number;
  screenY: number;
  payloads: FileRecordCopyPayload[];
  parentSectionId?: string;
  /**
   * Rail explorer drops land with the pointer over the sidebar, not the
   * board — place at the next free slot in the visible viewport instead
   * of mapping screen coords through `screenToFlowPosition`.
   */
  useViewportPlacement?: boolean;
  chatSession?: {
    flowSessionId: string;
    name: string;
  };
  entities?: Entity[];
}

export type CanvasBoardDropHandler = (
  ctx: CanvasBoardDropContext,
) => boolean | void | Promise<boolean | void>;

export type CanvasReparentHandler = (ctx: {
  canvasId: string;
  nodeIds: string[];
  targetParentSectionId: string | null;
}) => void | Promise<void>;

export type ChatAttachHandler = (records: FileRecord[]) => void | Promise<void>;

/**
 * Home/workspace composer drop target ("composer-attach" surface). The chat
 * may not exist yet at drop time: `ensureSession` mints (or returns) the
 * composer's flow session — mirroring how the attach button auto-creates one
 * on first attach — and `onAttached` receives the copied records to enqueue
 * as ready attachments on the pending message.
 */
export interface ComposerAttachTarget {
  ensureSession: () => Promise<string | null>;
  onAttached: ChatAttachHandler;
}

export interface WorkspaceDetailDropContext {
  sourceWorkspaceId: string;
  canvasMap: Map<string, import("@cohesive/api-client").Canvas>;
  fileRecordMap: Map<string, FileRecord>;
  /** Inbox items keyed by inbox-item id — resolves `chat:` selection ids into
   *  chat-session drag payloads. Optional: surfaces without draggable chats
   *  (the index page) don't provide it. */
  inboxItemMap?: Map<string, import("@cohesive/api-client").InboxItem>;
}

interface TransferDndRegistryValue {
  setBoardDropHandler: (handler: CanvasBoardDropHandler | null) => void;
  setReparentHandler: (handler: CanvasReparentHandler | null) => void;
  setChatAttachHandler: (handler: ChatAttachHandler | null) => void;
  registerComposerAttachTarget: (
    scopeKey: string,
    target: ComposerAttachTarget,
  ) => void;
  unregisterComposerAttachTarget: (scopeKey: string) => void;
  registerWorkspaceDetailContext: (
    ownerId: number,
    ctx: WorkspaceDetailDropContext,
  ) => void;
  unregisterWorkspaceDetailContext: (ownerId: number) => void;
  allocateWorkspaceDetailOwnerId: () => number;
}

const TransferDndRegistryContext =
  createContext<TransferDndRegistryValue | null>(null);

const WorkspaceDetailDropReadContext =
  createContext<WorkspaceDetailDropContext | null>(null);

export function useWorkspaceDetailDropContext(): WorkspaceDetailDropContext | null {
  return useContext(WorkspaceDetailDropReadContext);
}

export function useRegisterCanvasBoardDropHandler(
  handler: CanvasBoardDropHandler,
): void {
  const registry = useContext(TransferDndRegistryContext);
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!registry) return;
    registry.setBoardDropHandler((ctx) => ref.current(ctx));
    return () => registry.setBoardDropHandler(null);
  }, [registry]);
}

export function useRegisterCanvasReparentHandler(
  handler: CanvasReparentHandler,
): void {
  const registry = useContext(TransferDndRegistryContext);
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!registry) return;
    registry.setReparentHandler((ctx) => ref.current(ctx));
    return () => registry.setReparentHandler(null);
  }, [registry]);
}

export function useRegisterChatAttachHandler(handler: ChatAttachHandler): void {
  const registry = useContext(TransferDndRegistryContext);
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!registry) return;
    registry.setChatAttachHandler((ctx) => ref.current(ctx));
    return () => registry.setChatAttachHandler(null);
  }, [registry]);
}

export function useRegisterComposerAttachTarget(
  scopeKey: string,
  target: ComposerAttachTarget,
): void {
  const registry = useContext(TransferDndRegistryContext);
  const ref = useRef(target);
  ref.current = target;

  useEffect(() => {
    if (!registry) return;
    registry.registerComposerAttachTarget(scopeKey, {
      ensureSession: () => ref.current.ensureSession(),
      onAttached: (records) => ref.current.onAttached(records),
    });
    return () => registry.unregisterComposerAttachTarget(scopeKey);
  }, [registry, scopeKey]);
}

export function useRegisterWorkspaceDetailDropContext(
  ctx: WorkspaceDetailDropContext | null,
): void {
  const registry = useContext(TransferDndRegistryContext);
  const ownerIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!registry) return;
    if (!ctx) {
      if (ownerIdRef.current !== null) {
        registry.unregisterWorkspaceDetailContext(ownerIdRef.current);
        ownerIdRef.current = null;
      }
      return;
    }
    if (ownerIdRef.current === null) {
      ownerIdRef.current = registry.allocateWorkspaceDetailOwnerId();
    }
    registry.registerWorkspaceDetailContext(ownerIdRef.current, ctx);
    return () => {
      if (ownerIdRef.current !== null) {
        registry.unregisterWorkspaceDetailContext(ownerIdRef.current);
        ownerIdRef.current = null;
      }
    };
  }, [registry, ctx]);
}

function pickWorkspaceDetailDropContext(
  owners: Map<number, WorkspaceDetailDropContext>,
): WorkspaceDetailDropContext | null {
  let fallback: WorkspaceDetailDropContext | null = null;
  for (const ctx of owners.values()) {
    if (ctx.fileRecordMap.size > 0) return ctx;
    if (!fallback && ctx.sourceWorkspaceId) fallback = ctx;
  }
  return fallback;
}

/** @deprecated Use `TransferDndProvider`. */
export const CanvasDndProvider = TransferDndProvider;

/**
 * App-wide transfer `DndContext` for rail, canvas, workspace detail,
 * and chat composer drops. Native OS file drops use a separate channel.
 */
export function TransferDndProvider({ children }: { children: ReactNode }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const boardHandlerRef = useRef<CanvasBoardDropHandler | null>(null);
  const reparentHandlerRef = useRef<CanvasReparentHandler | null>(null);
  const chatAttachHandlerRef = useRef<ChatAttachHandler | null>(null);
  const composerAttachTargetsRef = useRef(
    new Map<string, ComposerAttachTarget>(),
  );
  const workspaceDetailRef = useRef<WorkspaceDetailDropContext | null>(null);
  const workspaceDetailOwnersRef = useRef(
    new Map<number, WorkspaceDetailDropContext>(),
  );
  const nextWorkspaceDetailOwnerIdRef = useRef(0);
  const [workspaceDetailCtx, setWorkspaceDetailCtxState] =
    useState<WorkspaceDetailDropContext | null>(null);

  const syncWorkspaceDetailContext = useCallback(() => {
    const next = pickWorkspaceDetailDropContext(
      workspaceDetailOwnersRef.current,
    );
    workspaceDetailRef.current = next;
    setWorkspaceDetailCtxState(next);
  }, []);

  const registerWorkspaceDetailContext = useCallback(
    (ownerId: number, ctx: WorkspaceDetailDropContext) => {
      workspaceDetailOwnersRef.current.set(ownerId, ctx);
      syncWorkspaceDetailContext();
    },
    [syncWorkspaceDetailContext],
  );
  const unregisterWorkspaceDetailContext = useCallback(
    (ownerId: number) => {
      workspaceDetailOwnersRef.current.delete(ownerId);
      syncWorkspaceDetailContext();
    },
    [syncWorkspaceDetailContext],
  );
  const allocateWorkspaceDetailOwnerId = useCallback(() => {
    nextWorkspaceDetailOwnerIdRef.current += 1;
    return nextWorkspaceDetailOwnerIdRef.current;
  }, []);

  const setBoardDropHandler = useCallback(
    (handler: CanvasBoardDropHandler | null) => {
      boardHandlerRef.current = handler;
    },
    [],
  );
  const setReparentHandler = useCallback(
    (handler: CanvasReparentHandler | null) => {
      reparentHandlerRef.current = handler;
    },
    [],
  );
  const setChatAttachHandler = useCallback(
    (handler: ChatAttachHandler | null) => {
      chatAttachHandlerRef.current = handler;
    },
    [],
  );
  const registerComposerAttachTarget = useCallback(
    (scopeKey: string, target: ComposerAttachTarget) => {
      composerAttachTargetsRef.current.set(scopeKey, target);
    },
    [],
  );
  const unregisterComposerAttachTarget = useCallback((scopeKey: string) => {
    composerAttachTargetsRef.current.delete(scopeKey);
  }, []);

  const registryValue = useMemo<TransferDndRegistryValue>(
    () => ({
      setBoardDropHandler,
      setReparentHandler,
      setChatAttachHandler,
      registerComposerAttachTarget,
      unregisterComposerAttachTarget,
      registerWorkspaceDetailContext,
      unregisterWorkspaceDetailContext,
      allocateWorkspaceDetailOwnerId,
    }),
    [
      setBoardDropHandler,
      setReparentHandler,
      setChatAttachHandler,
      registerComposerAttachTarget,
      unregisterComposerAttachTarget,
      registerWorkspaceDetailContext,
      unregisterWorkspaceDetailContext,
      allocateWorkspaceDetailOwnerId,
    ],
  );

  const dispatcher = useTransferDispatcher({
    getBoardDropHandler: () => boardHandlerRef.current,
    getReparentHandler: () => reparentHandlerRef.current,
    getChatAttachHandler: () => chatAttachHandlerRef.current,
    getComposerAttachTarget: (scopeKey) =>
      composerAttachTargetsRef.current.get(scopeKey) ?? null,
    getWorkspaceDetailContext: () => workspaceDetailRef.current,
  });

  const collisionDetection = useMemo<CollisionDetection>(
    () => deepestPointerWithin,
    [],
  );

  return (
    <TransferDndRegistryContext.Provider value={registryValue}>
      <WorkspaceDetailDropReadContext.Provider value={workspaceDetailCtx}>
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          modifiers={[snapCenterToCursor]}
          onDragEnd={dispatcher.onDragEnd}
          onDragCancel={dispatcher.onDragCancel}
          autoScroll
        >
          {children}
          <DragOverlay
            dropAnimation={null}
            style={{ width: "auto", height: "auto" }}
          >
            <TransferDragPill />
          </DragOverlay>
        </DndContext>
      </WorkspaceDetailDropReadContext.Provider>
    </TransferDndRegistryContext.Provider>
  );
}
